// authFailReason — turn a raw auth error into a STABLE, shareable slug.
//
// Auth is the one place instrumentation is most tempted to leak: the messages
// coming back from Supabase and the providers carry the email that was typed,
// the provider's own prose, sometimes a token fragment. So no call site ever
// sends the message — it sends one of the slugs below, and a slug is the only
// thing a funnel ever needs. "where did the door refuse them", not "what did the
// door say". Anything unrecognised collapses to `unknown` rather than falling
// back to the raw text, because a fallback is exactly how a message leaks.
//
// Sibling of lib/auth-errors.ts: that one writes the sentence a human reads,
// this one writes the token a report groups by. They stay separate on purpose —
// the human copy gets reworded all the time, and a renamed reason silently
// breaks every saved breakdown built on it.

export type AuthFailReason =
  | 'rate_limited'
  | 'invalid_credentials'
  | 'link_expired'
  | 'email_unconfirmed'
  | 'provider_blocked'
  | 'callback_failed'
  | 'network'
  | 'email_missing'
  | 'unknown'

export function authFailReason(raw: string | null | undefined): AuthFailReason {
  if (!raw) return 'unknown'
  const s = String(raw).toLowerCase()
  if (/rate limit|too many/.test(s)) return 'rate_limited'
  if (/invalid login credentials|invalid password|wrong password/.test(s)) return 'invalid_credentials'
  if (s.includes('auth_callback_failed')) return 'callback_failed'
  if (/email not confirmed|confirm your email/.test(s)) return 'email_unconfirmed'
  // the in-app-browser / webview refusal — a real, common wall, and worth its own
  // slug because the fix is "open a real browser", not "try again".
  if (/disallowed_useragent|blocked inside|in-app browser|webview/.test(s)) return 'provider_blocked'
  if (/expired|no longer valid|otp|token/.test(s)) return 'link_expired'
  if (/network|failed to fetch|fetch failed|offline/.test(s)) return 'network'
  return 'unknown'
}
